import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const DAYS = [
  { value: "Monday", label_fr: "Lundi", label_en: "Monday" },
  { value: "Tuesday", label_fr: "Mardi", label_en: "Tuesday" },
  { value: "Wednesday", label_fr: "Mercredi", label_en: "Wednesday" },
  { value: "Thursday", label_fr: "Jeudi", label_en: "Thursday" },
  { value: "Friday", label_fr: "Vendredi", label_en: "Friday" },
  { value: "Saturday", label_fr: "Samedi", label_en: "Saturday" },
];

const TIMEZONES = [
  "Europe/Paris", "Europe/London", "America/New_York", "America/Los_Angeles",
  "America/Chicago", "Asia/Tokyo", "Asia/Singapore", "Australia/Sydney",
];

const emptyForm = {
  name_fr: "", name_en: "",
  grade_level: "", teacher: "",
  teacher_user_id: "",
  format: "1x90",
  day: "Monday", time: "17:00",
  day2: "Wednesday", time2: "17:00",
  timezone: "Europe/Paris", capacity: 0,
  description: "",
  track: "any",
  catalog_visible: true,
};

export default function AdminScheduling() {
  const { t, lang } = useLang();
  const { isSuperAdmin } = useAuth();
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openAssign, setOpenAssign] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [studentId, setStudentId] = useState("");
  const [teachers, setTeachers] = useState([]);

  const load = () => Promise.all([
    api.get("/courses"), api.get("/students"), api.get("/admin/teachers"),
  ])
    .then(([a, b, c]) => { setCourses(a.data); setStudents(b.data); setTeachers(c.data); })
    .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name_fr: c.name_fr || "", name_en: c.name_en || "",
      grade_level: c.grade_level || "", teacher: c.teacher || "",
      teacher_user_id: c.teacher_user_id || "",
      format: c.format || "1x90",
      day: c.day || "Monday", time: c.time || "17:00",
      day2: c.day2 || "Wednesday", time2: c.time2 || "17:00",
      timezone: c.timezone || "Europe/Paris",
      capacity: c.capacity || 0,
      description: c.description || "",
      track: c.track || "any",
      catalog_visible: c.catalog_visible !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name_fr.trim() || !form.name_en.trim() || !form.grade_level.trim()) {
      toast.error(lang === "fr" ? "Nom FR/EN et niveau requis" : "FR/EN name and grade required");
      return;
    }
    if (form.format === "2x45" && (!form.day2 || !form.time2)) {
      toast.error(lang === "fr" ? "Jour 2 et heure 2 requis pour 2×45 min" : "Day 2 and time 2 required for 2×45 min");
      return;
    }
    const payload = { ...form, capacity: parseInt(form.capacity) || 0 };
    if (payload.format === "1x90") { delete payload.day2; delete payload.time2; }
    // "any" track → null (i.e. universal — visible to both Fluent & Bridge)
    if (payload.track === "any") payload.track = null;
    // Empty teacher_user_id → null (no teacher account linked)
    if (!payload.teacher_user_id) payload.teacher_user_id = null;
    // Auto-fill text `teacher` from selected teacher account, if not set manually
    if (payload.teacher_user_id && !payload.teacher) {
      const t = teachers.find(x => x.id === payload.teacher_user_id);
      if (t) payload.teacher = `${t.first_name || ""} ${t.last_name || ""}`.trim();
    }
    try {
      if (editId) await api.patch(`/courses/${editId}`, payload);
      else await api.post(`/courses`, payload);
      toast.success(lang === "fr" ? "Enregistré" : "Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(lang === "fr"
      ? `Supprimer le cours « ${c.name_fr} » ? (Aucun élève inscrit requis.)`
      : `Delete course « ${c.name_en} » ? (No enrolled students allowed.)`)) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success(lang === "fr" ? "Supprimé" : "Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const assign = async () => {
    if (!studentId) return;
    try {
      await api.post("/courses/assign", { course_id: openAssign.id, student_id: studentId });
      toast.success(lang === "fr" ? "Assigné" : "Assigned");
      setOpenAssign(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dayLabel = (d) => DAYS.find(x => x.value === d)?.[lang === "fr" ? "label_fr" : "label_en"] || d;

  return (
    <div data-testid="admin-scheduling">
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="overline mb-1">OFA France · Admin</p>
          <h1 className="font-serif text-3xl tracking-tight">{t("admin.scheduling")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "fr"
              ? "Créez des cours en 1×90 min ou 2×45 min, avec jour, heure, fuseau et professeur."
              : "Create courses as 1×90 min or 2×45 min, with day, time, timezone and teacher."}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-course-btn">
          <Plus className="w-4 h-4 mr-1" />{lang === "fr" ? "Nouveau cours" : "New course"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => (
          <Card key={c.id} data-testid={`course-card-${c.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl tracking-tight">
                {c.name_fr}
                <div className="text-xs text-muted-foreground font-sans">{c.name_en}</div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{lang === "fr" ? "Niveau" : "Grade"}</span><span>{c.grade_level}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{lang === "fr" ? "Enseignant" : "Teacher"}</span><span>{c.teacher || "—"}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{lang === "fr" ? "Format" : "Format"}</span>
                <Badge variant="outline" className="text-[10px]">
                  {c.format === "2x45" ? "2×45 min" : "1×90 min"}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{lang === "fr" ? "Parcours" : "Track"}</span>
                <div className="flex items-center gap-1">
                  {c.track === "fluent" && <Badge className="text-[10px] bg-primary/15 text-primary border-primary/30">Fluent</Badge>}
                  {c.track === "bridge" && <Badge className="text-[10px] bg-accent/15 text-accent border-accent/30">Bridge</Badge>}
                  {(!c.track || c.track === "any") && <Badge variant="outline" className="text-[10px]">{lang === "fr" ? "Tous" : "All"}</Badge>}
                  {c.catalog_visible === false && (
                    <Badge variant="secondary" className="text-[10px]" title={lang === "fr" ? "Caché du catalogue parents" : "Hidden from parent catalog"}>
                      {lang === "fr" ? "Caché" : "Hidden"}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-xs bg-secondary/40 rounded p-2 space-y-0.5">
                <div>{dayLabel(c.day)} · {c.time}</div>
                {c.format === "2x45" && c.day2 && (
                  <div>{dayLabel(c.day2)} · {c.time2}</div>
                )}
                <div className="text-[10px] text-muted-foreground">{c.timezone}</div>
              </div>
              <div className="flex justify-between items-center pt-2 gap-2">
                <Badge variant="outline">{c.enrolled_student_ids?.length || 0}/{c.capacity}</Badge>
                {(c.waiting_list?.length || 0) > 0 && <Badge variant="secondary">WL {c.waiting_list.length}</Badge>}
                <div className="flex gap-1 ml-auto">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {isSuperAdmin && (
                    <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`delete-course-${c.id}`}
                            className="text-destructive hover:text-destructive">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" data-testid={`assign-${c.id}`} onClick={() => { setOpenAssign(c); setStudentId(""); }}>
                    {lang === "fr" ? "Assigner" : "Assign"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editId ? (lang === "fr" ? "Modifier le cours" : "Edit course")
                      : (lang === "fr" ? "Nouveau cours" : "New course")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field><Label>{lang === "fr" ? "Nom (FR)" : "Name (FR)"}</Label>
                <Input data-testid="course-name-fr" value={form.name_fr} onChange={(e) => setForm(f => ({ ...f, name_fr: e.target.value }))} /></Field>
              <Field><Label>{lang === "fr" ? "Nom (EN)" : "Name (EN)"}</Label>
                <Input data-testid="course-name-en" value={form.name_en} onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))} /></Field>
              <Field><Label>{lang === "fr" ? "Niveau" : "Grade"}</Label>
                <Input data-testid="course-grade" placeholder="CE2 · 6e · 2nde…" value={form.grade_level} onChange={(e) => setForm(f => ({ ...f, grade_level: e.target.value }))} /></Field>
              <Field><Label>{lang === "fr" ? "Enseignant (texte)" : "Teacher (text)"}</Label>
                <Input data-testid="course-teacher" value={form.teacher} onChange={(e) => setForm(f => ({ ...f, teacher: e.target.value }))} /></Field>
            </div>
            <Field>
              <Label>{lang === "fr" ? "Compte enseignant titulaire" : "Assigned teacher account"}</Label>
              <Select
                value={form.teacher_user_id || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, teacher_user_id: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger data-testid="course-teacher-account"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— {lang === "fr" ? "Aucun (légacy texte)" : "None (legacy text)"} —</SelectItem>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.first_name} {t.last_name} · {t.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">
                {lang === "fr"
                  ? "Lie le cours à un vrai compte enseignant — donne accès au dashboard prof."
                  : "Links the course to a real teacher account — unlocks teacher dashboard access."}
              </div>
            </Field>
            <Field>
              <Label>{lang === "fr" ? "Format hebdomadaire" : "Weekly format"}</Label>
              <Select value={form.format} onValueChange={(v) => setForm(f => ({ ...f, format: v }))}>
                <SelectTrigger data-testid="course-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1x90">{lang === "fr" ? "1 séance × 90 min" : "1 session × 90 min"}</SelectItem>
                  <SelectItem value="2x45">{lang === "fr" ? "2 séances × 45 min" : "2 sessions × 45 min"}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <Label>{lang === "fr" ? "Parcours (track)" : "Track"}</Label>
                <Select value={form.track} onValueChange={(v) => setForm(f => ({ ...f, track: v }))}>
                  <SelectTrigger data-testid="course-track"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{lang === "fr" ? "Tous (Fluent + Bridge)" : "All (Fluent + Bridge)"}</SelectItem>
                    <SelectItem value="fluent">Fluent</SelectItem>
                    <SelectItem value="bridge">Bridge</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {lang === "fr"
                    ? "Filtre quels parents voient ce cours en admission"
                    : "Filters which parents see this course in admission"}
                </div>
              </Field>
              <Field>
                <Label>{lang === "fr" ? "Visible au catalogue" : "Visible in catalog"}</Label>
                <Select
                  value={form.catalog_visible ? "yes" : "no"}
                  onValueChange={(v) => setForm(f => ({ ...f, catalog_visible: v === "yes" }))}
                >
                  <SelectTrigger data-testid="course-catalog-visible"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">{lang === "fr" ? "Oui — sélectionnable par les parents" : "Yes — parent-selectable"}</SelectItem>
                    <SelectItem value="no">{lang === "fr" ? "Non — cours interne / test" : "No — internal / test course"}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <Label>{lang === "fr" ? "Jour 1" : "Day 1"}</Label>
                <Select value={form.day} onValueChange={(v) => setForm(f => ({ ...f, day: v }))}>
                  <SelectTrigger data-testid="course-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map(d => <SelectItem key={d.value} value={d.value}>{lang === "fr" ? d.label_fr : d.label_en}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field>
                <Label>{lang === "fr" ? "Heure 1" : "Time 1"}</Label>
                <Input data-testid="course-time" type="time" value={form.time} onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))} />
              </Field>
              {form.format === "2x45" && (
                <>
                  <Field>
                    <Label>{lang === "fr" ? "Jour 2" : "Day 2"}</Label>
                    <Select value={form.day2} onValueChange={(v) => setForm(f => ({ ...f, day2: v }))}>
                      <SelectTrigger data-testid="course-day2"><SelectValue /></SelectTrigger>
                      <SelectContent>{DAYS.map(d => <SelectItem key={d.value} value={d.value}>{lang === "fr" ? d.label_fr : d.label_en}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <Label>{lang === "fr" ? "Heure 2" : "Time 2"}</Label>
                    <Input data-testid="course-time2" type="time" value={form.time2} onChange={(e) => setForm(f => ({ ...f, time2: e.target.value }))} />
                  </Field>
                </>
              )}
              <Field>
                <Label>{lang === "fr" ? "Fuseau horaire" : "Timezone"}</Label>
                <Select value={form.timezone} onValueChange={(v) => setForm(f => ({ ...f, timezone: v }))}>
                  <SelectTrigger data-testid="course-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field>
                <Label>{lang === "fr" ? "Capacité" : "Capacity"}</Label>
                <Input data-testid="course-cap" type="number" placeholder={lang === "fr" ? "auto (4 / 6 / 8)" : "auto (4 / 6 / 8)"} value={form.capacity || ""} onChange={(e) => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || 0 }))} />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {lang === "fr" ? "GS–CE2 : 4 · CM1–4e : 6 · 3e–Tle : 8 · 0 = auto" : "GS–CE2: 4 · CM1–4e: 6 · 3e–Tle: 8 · 0 = auto"}
                </div>
              </Field>
            </div>
            <Button data-testid="course-save" onClick={save} className="w-full">
              {lang === "fr" ? "Enregistrer" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign student */}
      <Dialog open={!!openAssign} onOpenChange={(v) => !v && setOpenAssign(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lang === "fr" ? `Assigner un élève à ${openAssign?.name_fr}` : `Assign student to ${openAssign?.name_en}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="assign-student-select"><SelectValue placeholder={lang === "fr" ? "Choisir un élève" : "Select student"} /></SelectTrigger>
              <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.grade})</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="assign-confirm" onClick={assign} className="w-full">
              {lang === "fr" ? "Confirmer" : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ children }) { return <div>{children}</div>; }
